import { Link, useToast } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { RequiredIntermediateFieldNames } from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, DataLake } from '@finley-technologies/finley-types';

import {
  AthenaTablePropertyGQLType,
  useGetCreditFacilityWithCurrentCreditAgreementQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

// ======= Athena Column Name Utils =======//

// @jendevelops TODO: Might move this to common where it can be used by any package that needs to consume response tapes from Athena

const SuffixCaptureGroupRegExp = /^.*\..*\.(.*)$/;

// returns the suffixes from athena column name
const getSuffix = (columnName: string): string => {
  const match = columnName.match(SuffixCaptureGroupRegExp);
  if (match) {
    return match[1];
  }
  return '';
};

const prefixAndSuffixNames = {
  af: 'Assignment Fields',
  bbf: 'Borrowingbase Fields',
  bf: 'Base Fields',
  c: 'Concentration',
  ca: 'Concentration Amount',
  cffm: 'Credit Facility Fields Map',
  cl: 'Concentration Limit',
  clf: 'Concentration Limit Fields',
  clk: 'Concentration Limit Kickout',
  clr: 'Concentration Limit Reasons',
  cs: 'Compliance Status',
  dif: 'Dynamic Intermediate Fields',
  ef: 'Eligibility Fields',
  es: 'Eligibility Status',
  ess: 'Eligibility Statuses',
  nf: 'Normalized Fields',
  ppf: 'Post Processed Fields',
  rif: 'Required Intermediate Fields',
  vf: 'Valuation Fields',
  wr: 'Waive Reasons',
} as const;

type AthenaReceivableResponseTapePrefixAndSuffixNames = keyof typeof prefixAndSuffixNames;

const isAthenaReceivableResponseTapePrefixAndSuffixName = (
  test: any,
): test is AthenaReceivableResponseTapePrefixAndSuffixNames => {
  if (typeof test !== 'string') {
    return false;
  }
  return Object.keys(prefixAndSuffixNames).some((field) => field === test);
};

type GenerateFieldRegExpParams = {
  prefix: string;
  fields: string[];
  suffixes?: string[];
  initialVisibility?: (columnName: string) => boolean;
};

export type CaIndependentResponseTapeField =
  | 'assignmentFields'
  | 'borrowingBaseFields'
  | 'concentrationLimitFields'
  | 'eligibilityFields'
  | 'postProcessedFields'
  | 'valuationFields';

export type CaDependentResponseTapeField =
  | 'baseFields'
  | 'requiredIntermediateFields'
  | 'dynamicIntermediateFields'
  | 'eligibilityStatusesFields'
  | 'concentrationLimitReasonsFields';

const generalResponseTapeMetadata = {
  loanproductid: { label: 'Loan Product Id', isInitiallyVisible: false },
  externalreceivableid: {
    label: 'External Receivable Id',
    isInitiallyVisible: true,
  },
  finleyreceivableid: {
    label: 'Finley Receivable Id',
    isInitiallyVisible: false,
  },
  orgslug: { label: 'Organization Slug', isInitiallyVisible: false },
  tableprefix: { label: 'Table Prefix', isInitiallyVisible: false },
  tablename: { label: 'Table Name', isInitiallyVisible: false },
  reportingdate: { label: 'Reporting Date', isInitiallyVisible: true },
  fileversion: { label: 'File Version', isInitiallyVisible: false },
} as const;

type GeneralResponseTapeMetadata = keyof typeof generalResponseTapeMetadata;

type GeneralResponseTapeMetadataFieldType = 'generalResponseTapeMetadata';

const generalResponseTapeMetadataFields = Object.keys(
  generalResponseTapeMetadata,
) as GeneralResponseTapeMetadata[];

type FieldType =
  | CaIndependentResponseTapeField
  | CaDependentResponseTapeField
  | GeneralResponseTapeMetadataFieldType;

const isGeneralResponseTapeMetadata = (
  test: any,
): test is GeneralResponseTapeMetadata => {
  if (typeof test !== 'string') {
    return false;
  }
  return generalResponseTapeMetadataFields.some((field) => field === test);
};

/**
 * CA Independent Response Tape Fields
 */

export const caIndependentResponseTapeFieldsMap: Record<
  CaIndependentResponseTapeField,
  GenerateFieldRegExpParams
> = {
  assignmentFields: {
    prefix: 'af',
    suffixes: undefined,
    fields: [
      'Assigned At',
      'Credit Facility Id',
      'Reporting Date',
      'File Version',
      'Purchase Price',
      'Purchase Percentage Rate',
    ],
  },
  borrowingBaseFields: {
    prefix: 'bbf',
    suffixes: undefined,
    fields: ['Advance Rate', 'Borrowing Base Amount'],
  },
  concentrationLimitFields: {
    prefix: 'clf',
    suffixes: undefined,
    fields: ['Eligible Exposure less Concentration Limit Kickouts'],
  },
  eligibilityFields: {
    prefix: 'ef',
    suffixes: undefined,
    fields: ['Is Eligible', 'Eligibility Kickout', 'Eligible Exposure'],
  },
  postProcessedFields: {
    prefix: 'ppf',
    suffixes: undefined,
    fields: [
      'Original Owner Id',
      'Preassignment Credit Facility Id',
      'Normalized Exposure',
      'Performing Exposure',
    ],
  },
  valuationFields: {
    prefix: 'vf',
    suffixes: undefined,
    fields: ['Valuation', 'Valuation Rate'],
  },
};

const caIndependentResponseTapeFields = Object.keys(
  caIndependentResponseTapeFieldsMap,
) as CaIndependentResponseTapeField[];

const isCaIndependentResponseTapeField = (
  test: any,
): test is CaIndependentResponseTapeField =>
  caIndependentResponseTapeFields.some((field) => field === test);

/**
 * CA Dependent Response Tape Fields
 */

export const caDependentResponseTapeFieldsMap: Record<
  CaDependentResponseTapeField,
  Omit<GenerateFieldRegExpParams, 'fields'>
> = {
  baseFields: {
    prefix: 'bf',
    suffixes: undefined,
    initialVisibility: (_columnName: string) => true,
  },
  requiredIntermediateFields: {
    prefix: 'rif',
    suffixes: undefined,
    initialVisibility: (_columnName: string) => true,
  },
  dynamicIntermediateFields: {
    prefix: 'dif',
    suffixes: undefined,
    initialVisibility: (_columnName: string) => true,
  },
  eligibilityStatusesFields: {
    prefix: 'ess',
    suffixes: ['es'],
  },
  concentrationLimitReasonsFields: {
    prefix: 'clr',
    suffixes: ['cs', 'cl', 'ca', 'clk', 'c', 'wr'],
    initialVisibility: (columnName: string) => {
      const suffix = getSuffix(columnName);
      return suffix === 'clk';
    },
  },
};

const caDependentResponseTapeFields = Object.keys(
  caDependentResponseTapeFieldsMap,
) as CaDependentResponseTapeField[];

const isCaDependentResponseTapeField = (
  test: any,
): test is CaDependentResponseTapeField =>
  caDependentResponseTapeFields.some((field) => field === test);

/**
 * Returns a field name without spaces and all lower case
 * Replaces parentheses with escaped backslashes before parentheses to prevent regex from breaking
 */
export const readableLabelToAthenaColumnName = (fieldName: string): string =>
  fieldName
    .replace(/\s+/g, '')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .toLowerCase();

export const generateFieldRegExp = ({
  prefix,
  fields,
  suffixes,
}: GenerateFieldRegExpParams) => {
  finleyAssert(fields.length > 0, 'Fields must have at least one element');
  return new RegExp(
    `^${prefix}\\.(${fields.map(readableLabelToAthenaColumnName).join('|')})${
      suffixes ? `\\.(${suffixes.join('|')})` : ''
    }$`,
  );
};

export const getColumnFieldType = ({
  test,
  caFields,
}: {
  test: string;
  caFields: Record<CaDependentResponseTapeField, string[]>;
}): FieldType | null => {
  if (typeof test !== 'string') {
    return null;
  }

  if (isGeneralResponseTapeMetadata(test)) {
    return 'generalResponseTapeMetadata';
  }

  for (const fieldType of caIndependentResponseTapeFields) {
    if (
      test.match(
        generateFieldRegExp(caIndependentResponseTapeFieldsMap[fieldType]),
      )
    ) {
      return fieldType;
    }
  }

  for (const fieldType of caDependentResponseTapeFields) {
    if (
      caFields[fieldType].length === 0 ||
      test.match(
        generateFieldRegExp({
          ...caDependentResponseTapeFieldsMap[fieldType],
          fields: caFields[fieldType],
        }),
      )
    ) {
      return fieldType;
    }
  }

  return null;
};

// strips out the prefix and suffixes from athena column name
const stripAthenaColumnName = (columnName: string): string => {
  const match = columnName.split('.');
  // must be >= 2 because can have prefix + suffixes
  if (match.length >= 2) {
    return match[1];
  }
  return columnName;
};

/**
 * Converts athena column name to readable label for headers/ tooltips
 * @param fields expected fields for section
 * @param fieldName current athena column name
 * @returns
 */
export const athenaColumnToReadableLabel = ({
  fields,
  columnName,
}: {
  fields: string[];
  columnName: string;
}) => {
  let output = '';

  const field = fields.find(
    (currField) =>
      readableLabelToAthenaColumnName(currField) ===
      stripAthenaColumnName(columnName),
  );

  if (field) {
    output = field;
  }

  const suffix = getSuffix(columnName);

  if (field && isAthenaReceivableResponseTapePrefixAndSuffixName(suffix)) {
    output = `${output} (${prefixAndSuffixNames[suffix]})`;
  }

  return output || columnName;
};

export const getAthenaColumnReadableLabel = ({
  columnName,
  fieldType,
  caFields,
}: {
  columnName: string;
  fieldType: FieldType;
  caFields: Record<CaDependentResponseTapeField, string[]>;
}): string => {
  if (fieldType === 'generalResponseTapeMetadata') {
    return generalResponseTapeMetadata[
      columnName as GeneralResponseTapeMetadata
    ].label;
  }

  if (isCaIndependentResponseTapeField(fieldType)) {
    return athenaColumnToReadableLabel({
      fields: caIndependentResponseTapeFieldsMap[fieldType].fields,
      columnName,
    });
  }
  if (isCaDependentResponseTapeField(fieldType)) {
    return athenaColumnToReadableLabel({
      fields: caFields[fieldType],
      columnName,
    });
  }

  // defaults to column name if you can't find the label for it
  return columnName;
};

export const aggregateCaDependentFields = (
  loanProductMechanics: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[],
): Record<CaDependentResponseTapeField, string[]> => {
  const agg = {
    baseFields: new Set<string>(),
    requiredIntermediateFields: new Set<string>(),
    dynamicIntermediateFields: new Set<string>(),
    eligibilityStatusesFields: new Set<string>(),
    concentrationLimitReasonsFields: new Set<string>(),
  };
  loanProductMechanics.reduce((acc, currLp) => {
    const {
      eligibilityTests,
      concentrationLimits,
      _config_: { baseFieldConfigs, intermediateCalcConfigs },
    } = currLp;

    baseFieldConfigs.forEach(({ field }) => acc.baseFields.add(field));
    const requiredIntermediateFields: string[] = RequiredIntermediateFieldNames;

    requiredIntermediateFields.forEach((field) =>
      acc.requiredIntermediateFields.add(field),
    );

    intermediateCalcConfigs.forEach(({ field }) => {
      if (!requiredIntermediateFields.includes(field)) {
        acc.dynamicIntermediateFields.add(field);
      }
    });

    eligibilityTests.forEach(({ name }) => {
      acc.eligibilityStatusesFields.add(name);
    });

    concentrationLimits.forEach(({ name }) => {
      acc.concentrationLimitReasonsFields.add(name);
    });

    return acc;
  }, agg);

  const output: Record<CaDependentResponseTapeField, string[]> = {
    baseFields: [],
    requiredIntermediateFields: [],
    dynamicIntermediateFields: [],
    eligibilityStatusesFields: [],
    concentrationLimitReasonsFields: [],
  };

  Object.entries(agg).forEach(([key, value]: [string, Set<string>]) => {
    output[key as CaDependentResponseTapeField] = Array.from(value);
  });
  return output;
};

const isInitiallyVisible = ({
  columnName,
  fieldType,
}: {
  columnName: string;
  fieldType: FieldType | null;
}): boolean => {
  if (isGeneralResponseTapeMetadata(columnName)) {
    return generalResponseTapeMetadata[columnName].isInitiallyVisible;
  }

  if (isCaIndependentResponseTapeField(fieldType)) {
    return true;
  }
  if (isCaDependentResponseTapeField(fieldType)) {
    const { initialVisibility } = caDependentResponseTapeFieldsMap[fieldType];
    return initialVisibility ? initialVisibility(columnName) : false;
  }

  // unexpected properties are hidden by default
  return false;
};

export type ColumnDefMeta = {
  key: string;
  fieldType: FieldType | null;
  initialVisibility: boolean;
  dataType: DataLake.AthenaTablePropertyTypeUnion;
};

export type ReceivablesTableColumnDef<T = any> = ColumnDef<T> & {
  header: string;
  meta: ColumnDefMeta;
};

const dataTypesToRound = ['float', 'double', 'decimal'];

/**
 * Transforms athena column names to column config for react-table for ReceivablesDashboard specific fields
 *
 * Transformation is not exhaustive, only handles fields that are currently used in ReceivablesDashboard
 *
 */
export const transformAthenaColumnsToColumnConfig = ({
  isLenderPage = true,
  columnProperties,
  loanProductMechanics,
}: {
  isLenderPage?: boolean;
  columnProperties: AthenaTablePropertyGQLType[];
  loanProductMechanics: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[];
}): ReceivablesTableColumnDef[] => {
  if (columnProperties.length === 0) {
    return [];
  }
  const caDependentFields = aggregateCaDependentFields(loanProductMechanics);

  const columns: ReceivablesTableColumnDef[] = columnProperties.map(
    ({ name: columnName, type: dataType }) => {
      const fieldType = getColumnFieldType({
        test: columnName,
        caFields: caDependentFields,
      });
      const readableLabel = fieldType
        ? getAthenaColumnReadableLabel({
            columnName,
            fieldType,
            caFields: caDependentFields,
          })
        : columnName;
      return {
        id: columnName,
        header: readableLabel,
        accessorFn: (row: any) => row[columnName],
        meta: {
          key: columnName,
          fieldType,
          initialVisibility: isInitiallyVisible({ columnName, fieldType }),
          dataType,
        },
        ...(columnName === 'externalreceivableid' && {
          cell: function externalReceivableIdCell({ row }) {
            const { externalreceivableid, finleyreceivableid } = row.original;
            const receivableDetailsLink = `/${
              isLenderPage ? 'portfolio/' : ''
            }receivables/${finleyreceivableid}`;
            return (
              <Link
                color={colors.blue[400]}
                textDecoration="underline"
                href={receivableDetailsLink}
                _hover={{ color: colors.blue[600] }}
              >
                {externalreceivableid}
              </Link>
            );
          },
        }),
        ...(dataTypesToRound.includes(dataType) && {
          cell: ({ getValue }) => {
            const cellValue = getValue();
            if (typeof cellValue === 'number') {
              return parseFloat(cellValue.toFixed(3));
            }
            return cellValue;
          },
        }),
      };
    },
  );
  const sortedColumns: ReceivablesTableColumnDef[] = [];

  return columns.reduce((acc, currColumn) => {
    if (currColumn.id === 'externalreceivableid') {
      return [currColumn, ...acc];
    }
    if (currColumn.id === 'reportingdate') {
      return acc[0]?.id === 'externalreceivableid'
        ? [acc[0], currColumn, ...acc.slice(1)]
        : [currColumn, ...acc];
    }
    return [...acc, currColumn];
  }, sortedColumns);
};

export const useTransformAthenaColumnsToColumnConfig = ({
  isLenderPage = true,
  columnProperties,
  creditFacilityId,
}: {
  isLenderPage?: boolean;
  creditFacilityId: string;
  columnProperties: AthenaTablePropertyGQLType[];
}): { columns: ReceivablesTableColumnDef[]; isLoading: boolean } => {
  const toast = useToast();
  const {
    data,
    loading: isLoading,
  } = useGetCreditFacilityWithCurrentCreditAgreementQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  const columns = useMemo(() => {
    if (isLoading || !data) {
      return [];
    }
    const creditAgreement = parseCreditAgreementFullArtifact(
      data?.creditFacility?.currentCreditAgreement?.creditAgreement,
    );
    const loanProductMechanics =
      creditAgreement?.borrowingBaseMechanics.calcAgentMechanics
        .loanProductMechanics ?? [];

    return transformAthenaColumnsToColumnConfig({
      isLenderPage,
      loanProductMechanics,
      columnProperties,
    });
  }, [isLoading, data, isLenderPage, columnProperties]);

  return { columns, isLoading };
};
